import { useEffect, useMemo, useState } from 'react';
import { Divider } from 'antd';

import SortArrowsIcon from 'assets/svg/SortArrowsIcon';
import PpaSplitSearchInput from 'components/molecules/PPA/PpaSplitSearchInput/PpaSplitSearchInput';

import type { DimensionSortOrder } from './DimensionColumnHeader';
import { GenericCheckboxControl } from 'components/molecules/PPA/commons';
import {
    StyledFilterApplyButton,
    StyledFilterResetButton as StyledFilterGreyButton,
} from '../SimulatorFilterPanel/SimulatorFilterPanel.styles';

import {
    ppaSimTableColFilterApplyBtnTestId,
    ppaSimTableColFilterCancelBtnTestId,
    ppaSimTableColFilterOptionTestId,
    ppaSimTableColFilterPanelTestId,
    ppaSimTableColFilterResetBtnTestId,
    ppaSimTableColFilterSearchSubmitTestId,
    ppaSimTableColFilterSearchTestId,
    ppaSimTableColFilterSelectAllTestId,
    ppaSimTableColFilterSortAscTestId,
    ppaSimTableColFilterSortDescTestId,
} from 'constants/ppaTestIds';
import type { SimulationColumnFilterSortConfig } from 'types/ppaSimulationMeta';

export type ColumnFilterSortValue = {
    sortOrder: DimensionSortOrder | null;
    selectedValues: string[];
};

type ColumnFilterSortPanelProps = {
    columnKey: string;
    options: string[];
    value: ColumnFilterSortValue;
    labels: SimulationColumnFilterSortConfig;
    onCancel: () => void;
    onApply: (value: ColumnFilterSortValue) => void;
};

const ALL_OPTION_LABEL = '(All)';

const ColumnFilterSortPanel = ({
    columnKey,
    options,
    value,
    labels,
    onCancel,
    onApply,
}: ColumnFilterSortPanelProps) => {
    const [draftValue, setDraftValue] = useState<ColumnFilterSortValue>(value);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setDraftValue(value);
        setSearch('');
    }, [value]);

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.toLowerCase().includes(query));
    }, [options, search]);

    const selectedSet = useMemo(() => new Set(draftValue.selectedValues), [draftValue.selectedValues]);
    const areAllVisibleSelected =
        visibleOptions.length > 0 && visibleOptions.every((option) => selectedSet.has(option));
    const areSomeVisibleSelected = visibleOptions.some((option) => selectedSet.has(option));

    const handleSortSelect = (sortOrder: DimensionSortOrder) => {
        setDraftValue((previous) => ({
            ...previous,
            sortOrder: previous.sortOrder === sortOrder ? null : sortOrder,
        }));
    };

    const handleToggleAll = (checked: boolean) => {
        setDraftValue((previous) => {
            const nextSelected = new Set(previous.selectedValues);
            visibleOptions.forEach((option) => {
                if (checked) nextSelected.add(option);
                else nextSelected.delete(option);
            });
            return { ...previous, selectedValues: [...nextSelected] };
        });
    };

    const handleToggleOption = (option: string, checked: boolean) => {
        setDraftValue((previous) => {
            const nextSelected = new Set(previous.selectedValues);
            if (checked) nextSelected.add(option);
            else nextSelected.delete(option);
            return { ...previous, selectedValues: [...nextSelected] };
        });
    };

    const handleReset = () => {
        setSearch('');
        setDraftValue({ sortOrder: null, selectedValues: [...options] });
    };

    const getSortButtonClass = (isSelected: boolean) =>
        `min-w-0 flex-1 !w-auto !shrink gap-2 !text-[14px] !font-semibold !leading-[22px] [&>span]:!ml-0 ${
            isSelected ? '!border-tableSortIconPurple !text-tableSortIconPurple' : '!text-blackTransparent88'
        }`;

    return (
        <div
            className="flex w-[360px] max-w-full flex-col gap-4 overflow-hidden rounded-lg border border-black/[0.06] bg-white py-4 shadow-moreActionsDropdown"
            data-testid={ppaSimTableColFilterPanelTestId(columnKey)}
        >
            <div className="flex flex-col gap-2 px-4">
                <p className="m-0 text-base font-semibold leading-6 text-blackTransparent88">{labels.sortLabel}</p>
                <div className="flex w-full min-w-0 gap-2">
                    <StyledFilterGreyButton
                        className={getSortButtonClass(draftValue.sortOrder === 'ascend')}
                        data-testid={ppaSimTableColFilterSortAscTestId(columnKey)}
                        onClick={() => handleSortSelect('ascend')}
                    >
                        <SortArrowsIcon direction="up" />
                        {labels.ascendingLabel}
                    </StyledFilterGreyButton>
                    <StyledFilterGreyButton
                        className={getSortButtonClass(draftValue.sortOrder === 'descend')}
                        data-testid={ppaSimTableColFilterSortDescTestId(columnKey)}
                        onClick={() => handleSortSelect('descend')}
                    >
                        <SortArrowsIcon direction="down" />
                        {labels.descendingLabel}
                    </StyledFilterGreyButton>
                </div>
            </div>
            <div className="px-4">
                <Divider className="!m-0" />
            </div>
            <div className="flex flex-col gap-2 px-4">
                <p className="m-0 text-base font-semibold leading-6 text-blackTransparent88">{labels.filterLabel}</p>
                <PpaSplitSearchInput
                    width="100%"
                    placeholder={labels.searchPlaceholder}
                    value={search}
                    onChange={setSearch}
                    ariaLabel="Search filters"
                    submitAriaLabel="Submit filter search"
                    inputTestId={ppaSimTableColFilterSearchTestId(columnKey)}
                    submitTestId={ppaSimTableColFilterSearchSubmitTestId(columnKey)}
                />
            </div>
            <div className="flex max-h-[230px] flex-col gap-1 overflow-y-auto px-4 text-sm leading-[22px] text-blackTransparent88 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-tableSortIconPurple [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-tableSortIconPurple [&_.ant-checkbox-indeterminate_.ant-checkbox-inner]:!border-tableSortIconPurple [&_.ant-checkbox-indeterminate_.ant-checkbox-inner]:!bg-tableSortIconPurple [&_.ant-checkbox-wrapper]:m-0">
                <GenericCheckboxControl
                    showIndeterminate
                    indeterminate={areSomeVisibleSelected && !areAllVisibleSelected}
                    checked={areAllVisibleSelected}
                    onChange={handleToggleAll}
                    dataTestId={ppaSimTableColFilterSelectAllTestId(columnKey)}
                >
                    {ALL_OPTION_LABEL}
                </GenericCheckboxControl>
                {visibleOptions.map((option) => (
                    <GenericCheckboxControl
                        key={option}
                        checked={selectedSet.has(option)}
                        onChange={(checked) => handleToggleOption(option, checked)}
                        dataTestId={ppaSimTableColFilterOptionTestId(columnKey, option)}
                    >
                        {option}
                    </GenericCheckboxControl>
                ))}
            </div>
            <div className="flex items-center justify-between px-4">
                <StyledFilterGreyButton
                    data-testid={ppaSimTableColFilterResetBtnTestId(columnKey)}
                    onClick={handleReset}
                >
                    {labels.resetLabel}
                </StyledFilterGreyButton>
                <div className="flex items-center gap-2">
                    <StyledFilterGreyButton
                        data-testid={ppaSimTableColFilterCancelBtnTestId(columnKey)}
                        onClick={onCancel}
                    >
                        {labels.cancelLabel}
                    </StyledFilterGreyButton>
                    <StyledFilterApplyButton
                        type="primary"
                        data-testid={ppaSimTableColFilterApplyBtnTestId(columnKey)}
                        onClick={() => onApply(draftValue)}
                    >
                        {labels.applyLabel}
                    </StyledFilterApplyButton>
                </div>
            </div>
        </div>
    );
};

export default ColumnFilterSortPanel;
